/* eslint-disable @ngrx/avoid-dispatching-multiple-actions-sequentially */
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, firstValueFrom } from 'rxjs';
import * as HydrationStatusActions from 'src/app/core/store/common/actions/hydration-status.action';
import * as UserProfileActions from 'src/app/core/store/nowaccount/actions/user-profile.actions';
import * as AccountManagerContactActions from 'src/app/core/store/onboarding/actions/account-manager-contact.actions';
import * as BusinessProfileActions from 'src/app/core/store/onboarding/actions/business-profile.actions';
import * as CreateAccountActions from 'src/app/core/store/onboarding/actions/create-account.actions';
import * as CustomerPortfolioActions from 'src/app/core/store/onboarding/actions/customer-portfolio.actions';
import * as EnrollmentActions from 'src/app/core/store/onboarding/actions/enrollment.actions';
import * as FinancialProfileActions from 'src/app/core/store/onboarding/actions/financial-profile.actions';
import { environment } from 'src/environments/environment';
import { AbstractHydrationService } from '../../abstracts/services/login/hydration.abstract';
import { handleHttpError } from '../../interceptors/handle-http-error';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { IAppState } from '../../store/common/interfaces/app-state.interface';
import {
  HydrationRoles,
  HydrationSources,
  IHydrationStatusStore,
} from '../../store/common/interfaces/hydration-status-store.interface';
import { IOnboardingHydration } from '../../store/common/interfaces/hydration.interface';
import { IUserProfileStore } from '../../store/nowaccount/interfaces/user-profile-store.interface';
import { ClearCustomerAction } from '../../store/onboarding/actions/customer-portfolio.actions';
import { IAccountManagerContactStore } from '../../store/onboarding/interfaces/account-manager-contact-store.interface';
import { IBusinessProfileStore } from '../../store/onboarding/interfaces/business-profile-store.interface';
import { ICreateAccountStore } from '../../store/onboarding/interfaces/create-account-store.interface';
import { ICustomerPortfolioStore } from '../../store/onboarding/interfaces/customer-portfolio-store.interface';
import { IEnrollmentStore } from '../../store/onboarding/interfaces/enrollment-store.interface';
import { IFinancialProfileStore } from '../../store/onboarding/interfaces/financial-profile-store.interface';
import { selectDashboardStateId } from '../../store/onboarding/reducers+selectors/enrollment.selector';
import {
  applyUserEmailToQueryParams,
  applyUserFirstNameToQueryParams,
  applyUserLastNameToQueryParams,
  applyUserProfileId,
} from '../../ui-helpers/functions/user-context-url.func';
import { KendoNotificationService } from '../common/kendo-notification.service';
import { LocalStorageService } from '../common/local-storage.service';
import { LoggerService } from '../common/logger.service';
import { FinancialProfileService } from '../onboarding/finacial-profile.service';

@Injectable({
  providedIn: 'root',
})
export class HydrationService extends AbstractHydrationService {
  private apiUrl = environment.apiUrl;
  private onboardingDashboardControllerSuffix: string =
    'Onboarding/Onboarding/dashboard/';
  private customerControllerSuffix: string = 'Onboarding/Onboarding/customer/';
  private accountDashboardControllerSuffix =
    'Dashboard/DashboardTiles/dashboard/';
  payload: ICustomerPortfolioStore;
  constructor(
    private http: HttpClient,
    private fps: FinancialProfileService,
    private store: Store<IAppState>,
    private logger: LoggerService,
    private kns: KendoNotificationService,
    private lss: LocalStorageService,
  ) {
    super();
  }

  async CheckHasClientAdditionalInfoRequest() {
    const dashboardStateId$ = this.store.select(selectDashboardStateId);
    let dashboardStateId = '-1';
    const sub = dashboardStateId$.subscribe(async (props) => {
      dashboardStateId = props.dashboardStateId;
    });
    sub.unsubscribe();
    let params = new HttpParams();
    params = applyUserProfileId(params, this.store);
    const url = `${this.apiUrl}${this.onboardingDashboardControllerSuffix}check-has-client-additional-info-request/${dashboardStateId}`;
    const response = await firstValueFrom(
      this.http.get<IApiResponse>(url, { params }).pipe(
        catchError((error: HttpErrorResponse) =>
          handleHttpError(error, this.logger, this.kns),
        ), // handle error if all request attempts fail
      ),
    );
    const result = response.data as boolean;
    this.store.dispatch(
      EnrollmentActions.HasClientAdditionalInfoRequestAction({
        hasClientAdditionalInfoRequest: result,
      }),
    );
    return result;
  }

  async HydrateOnboardingStore(
    hydrationRole: HydrationRoles,
    hydrationSource: HydrationSources,
  ): Promise<IOnboardingHydration> {
    let params = new HttpParams();
    params = applyUserProfileId(params, this.store);
    const url = `${this.apiUrl}${this.onboardingDashboardControllerSuffix}hydrate-onboarding`;
    const response = await firstValueFrom(
      this.http.post<IApiResponse>(url, null, { params }).pipe(
        catchError((error: HttpErrorResponse) =>
          handleHttpError(error, this.logger, this.kns),
        ), // handle error if all request attempts fail
      ),
    );

    const data = response.data as IOnboardingHydration;
    if (
      data.accountManagerContact_FirstName &&
      data.accountManagerContact_LastName &&
      data.accountManagerContact_Email &&
      data.accountManagerContact_PhoneNumber
    ) {
      this.store.dispatch(
        AccountManagerContactActions.AllAction({
          firstName: data.accountManagerContact_FirstName,
          lastName: data.accountManagerContact_LastName,
          email: data.accountManagerContact_Email,
          phoneNumber: data.accountManagerContact_PhoneNumber,
        }),
      );
    }
    this.store.dispatch(
      EnrollmentActions.AllAction({
        enrollmentId: data.enrollmentId,
        enrollmentStepId: data.enrollmentStepId,
        dashboardStateId: data.dashboardStateId,
        businessProfileStateId: data.businessProfileStateId,
        financialProfileStateId: data.financialProfileStateId,
        customerPortfolioStateId: data.customerPortfolioStateId,
        businessProfileStatusId: data.businessProfileStatusId,
        financialProfileStatusId: data.financialProfileStatusId,
        customerPortfolioStatusId: data.customerPortfolioStatusId,
        applicationReviewStatusId: data.applicationReviewStatusId,
        msaSignatureStatusId: data.msaSignatureStatusId,
        nowAccountActivationStatusId: data.nowAccountActivationStatusId,
        hasClientAdditionalInfoRequest: data.hasClientAdditionalInfoRequest,
      }),
    );
    this.store.dispatch(
      CreateAccountActions.AllAction({
        registeredCompanyName: data.registeredCompanyName,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        heardAboutUsId: data.heardAboutUsId,
        howDidYouHearAboutUs: data.howDidYouHearAboutUs,
        heardAboutUsDetails: data.heardAboutUsDetails,
        mobilePhoneNumber: data.mobilePhoneNumber,
        businessPhoneNumber: data.businessPhoneNumber,
      }),
    );
    this.store.dispatch(
      BusinessProfileActions.AllAction({
        businessType: data.businessType,
        businessIndustry: data.businessIndustry,
        businessDescription: data.businessDescription,
        customerTypes: data.customerTypes,
        stateOfOrg: data.stateOfOrg,
        dba: data.dba,
        companyAddress: data.companyAddress,
        annualRevenue: data.annualRevenue,
        companyWebsite: data.companyWebsite,
        yearFounded: data.yearFounded,
        employeeCount: data.employeeCount,
        invoicingMethod: data.invoicingMethod,
        companySearch: data.companySearch,
        supportMethods: data.supportMethods,
      }),
    );

    let invoiceAmountName = '';
    let invoiceTermName = '';

    await Promise.allSettled([
      (async () => {
        invoiceAmountName = await this.getInvoiceAmountName(
          data.monthlyInvoiceAmount.id,
        );
      })(),
      (async () => {
        invoiceTermName = await this.getInvoiceTermName(
          data.typicalInvoiceTerm.id,
        );
      })(),
    ]);
    this.store.dispatch(
      FinancialProfileActions.AllAction({
        invoiceAmount: {
          id: data.monthlyInvoiceAmount.id,
          value: invoiceAmountName,
        },
        invoiceTerm: { id: data.typicalInvoiceTerm.id, value: invoiceTermName },
        bankStatementsComplete: data.bankStatementsComplete,
        balanceSheetsComplete: data.balanceSheetsComplete,
        plStatementsComplete: data.plStatementsComplete,
        arAgingComplete: data.arAgingComplete,
        apAgingComplete: data.apAgingComplete,
      }),
    );

    this.dispatchHydationStatusStore({ hydrationRole, hydrationSource });

    return data;
  }

  async HydrateCustomerPortfolioStore(
    clientCustomerId: number,
  ): Promise<boolean> {
    this.store.dispatch(ClearCustomerAction());
    const url = `${this.apiUrl}${this.customerControllerSuffix}rehydrate-customer-state/${clientCustomerId}`;
    const response = await firstValueFrom(
      this.http.get<IApiResponse>(url).pipe(
        catchError((error: HttpErrorResponse) =>
          handleHttpError(error, this.logger, this.kns),
        ), // handle error if all request attempts fail
      ),
    );
    const data = response.data as ICustomerPortfolioStore;
    this.store.dispatch(
      CustomerPortfolioActions.AllAction({
        clientCustomerId: data.clientCustomerId,
        clientId: data.clientId,
        CustomerRefNumber: response.data.customerRefNumber,
        customerName: data.customerName,
        isFederal: data.isFederal,
        billingAddress: data.billingAddress,
        headquarterAddress: data.headquarterAddress,
        isHeadquarterAddress: data.isHeadquarterAddress,
        customerType: data.customerType,
        monthlyInvoiceAmount: data.monthlyInvoiceAmount,
        invoiceTerm: data.invoiceTerm,
        billingCountry: data.billingAddress.country,
        physicalAddress: data.physicalAddress,
        physicalCountry: data.physicalAddress.country,
        headquarterCountry: data.headquarterAddress.country,
        federalDocumentNumber: data.federalDocumentNumber,
      }),
    );

    this.payload = data;
    return true;
  }

  async HydrateAccountStore(
    hydrationRole: HydrationRoles,
    hydrationSource: HydrationSources,
  ): Promise<IOnboardingHydration> {
    let params = new HttpParams();
    params = applyUserProfileId(params, this.store);
    params = applyUserEmailToQueryParams(params, this.store);
    params = applyUserFirstNameToQueryParams(params, this.store);
    params = applyUserLastNameToQueryParams(params, this.store);
    const url = `${this.apiUrl}${this.accountDashboardControllerSuffix}hydrate-account`;
    const response = await firstValueFrom(
      this.http.get<IApiResponse>(url, { params }).pipe(
        catchError((error: HttpErrorResponse) =>
          handleHttpError(error, this.logger, this.kns),
        ), // handle error if all request attempts fail
      ),
    );

    const data = response.data as IOnboardingHydration;

    if (
      data.accountManagerContact_FirstName &&
      data.accountManagerContact_LastName &&
      data.accountManagerContact_Email
    ) {
      this.store.dispatch(
        AccountManagerContactActions.AllAction({
          firstName: data.accountManagerContact_FirstName,
          lastName: data.accountManagerContact_LastName,
          email: data.accountManagerContact_Email,
          phoneNumber: data.accountManagerContact_PhoneNumber,
        }),
      );
    }

    this.store.dispatch(
      CreateAccountActions.AllAction({
        registeredCompanyName: data.registeredCompanyName,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        heardAboutUsId: data.heardAboutUsId,
        howDidYouHearAboutUs: data.howDidYouHearAboutUs,
        heardAboutUsDetails: data.heardAboutUsDetails,
        mobilePhoneNumber: data.mobilePhoneNumber,
        businessPhoneNumber: data.businessPhoneNumber,
      }),
    );

    this.store.dispatch(
      CreateAccountActions.ClientNumberAction({
        clientNumber: data.clientNumber,
      }),
    );

    this.dispatchHydationStatusStore({ hydrationRole, hydrationSource });

    return data;
  }

  loadAppStoreViaLocalStorage() {
    console.log('hydration.service loadAppStoreViaLocalStorage');
    var appState = this.lss.getLocalData('state');
    console.log(JSON.stringify(appState));
    this.dispatchAll(appState);
  }

  private async getInvoiceAmountName(id: number | string) {
    const invoiceAmounts = await this.fps.loadInvoiceAmount();
    console.log('invoiceAmounts, ', invoiceAmounts);
    const tempArr = invoiceAmounts.filter(
      (invoiceAmount) => invoiceAmount.id === id,
    );
    if (tempArr.length > 0) return tempArr[0].value;
    return '';
  }

  private async getInvoiceTermName(id: number | string) {
    const invoiceTerms = await this.fps.loadInvoiceTerm();
    console.log('invoiceTerms, ', invoiceTerms);
    const tempArr = invoiceTerms.filter((invoiceTerm) => invoiceTerm.id === id);
    if (tempArr.length > 0) return `${tempArr[0].value} days`;
    return '';
  }

  private dispatchAll(stores: IAppState) {
    console.log('dispatchAll');
    console.log(JSON.stringify(stores));
    this.dispatchAccountManagerContactStore(stores.accountManagerContact);
    this.dispatchEnrollmentStore(stores.enrollment);
    this.dispatchCreateAccountStore(stores.createAccount);
    this.dispatchBusinessProfileStore(stores.businessProfile);
    this.dispatchFinancialProfileStore(stores.financialProfile);
    this.dispatchCustomerPortfolioStore(stores.customerPortfolio);
    this.dispatchHydationStatusStore(stores.hydrationStatus);
    this.dispatchUserProfileStore(stores.userProfile);
  }

  private dispatchAccountManagerContactStore(
    accountManagerContactStore: IAccountManagerContactStore,
  ) {
    console.log('dispatchAccountManagerContactStore');
    this.store.dispatch(
      AccountManagerContactActions.AllAction(accountManagerContactStore),
    );
  }

  private dispatchEnrollmentStore(enrollmentStore: IEnrollmentStore) {
    console.log('dispatchEnrollmentStore');
    const eStore: {
      enrollmentId: string;
      enrollmentStepId: number;
      dashboardStateId: number;
      businessProfileStateId: number;
      financialProfileStateId: number;
      customerPortfolioStateId: number;
      businessProfileStatusId: number;
      financialProfileStatusId: number;
      customerPortfolioStatusId: number;
      applicationReviewStatusId: number;
      msaSignatureStatusId: number;
      nowAccountActivationStatusId: number;
      hasClientAdditionalInfoRequest: boolean;
    } = {
      enrollmentId: enrollmentStore.enrollmentId,
      enrollmentStepId: enrollmentStore.enrollmentStepId,
      dashboardStateId: parseInt(enrollmentStore.dashboardStateId),
      businessProfileStateId: parseInt(enrollmentStore.businessProfileStateId),
      financialProfileStateId: parseInt(
        enrollmentStore.financialProfileStateId,
      ),
      customerPortfolioStateId: parseInt(
        enrollmentStore.customerPortfolioStateId,
      ),
      businessProfileStatusId: enrollmentStore.businessProfileStatusId,
      financialProfileStatusId: enrollmentStore.financialProfileStatusId,
      customerPortfolioStatusId: enrollmentStore.financialProfileStatusId,
      applicationReviewStatusId: enrollmentStore.applicationReviewStatusId,
      msaSignatureStatusId: enrollmentStore.msaSignatureStatusId,
      nowAccountActivationStatusId:
        enrollmentStore.nowAccountActivationStatusId,
      hasClientAdditionalInfoRequest:
        enrollmentStore.hasClientAdditionalInfoRequest,
    };
    this.store.dispatch(EnrollmentActions.AllAction(eStore));
  }

  private dispatchCreateAccountStore(createAccountStore: ICreateAccountStore) {
    console.log('dispatchCreateAccountStore');
    this.store.dispatch(CreateAccountActions.AllAction(createAccountStore));
  }

  private dispatchBusinessProfileStore(
    businessProfileStore: IBusinessProfileStore,
  ) {
    console.log('dispatchBusinessProfileStore');
    this.store.dispatch(BusinessProfileActions.AllAction(businessProfileStore));
  }

  private dispatchFinancialProfileStore(
    financialProfileStore: IFinancialProfileStore,
  ) {
    console.log('dispatchFinancialProfileStore');
    this.store.dispatch(
      FinancialProfileActions.AllAction(financialProfileStore),
    );
  }

  private dispatchCustomerPortfolioStore(
    customerPortfolioStore: ICustomerPortfolioStore,
  ) {
    console.log('dispatchCustomerPortfolioStore');
    this.store.dispatch(
      CustomerPortfolioActions.AllAction(customerPortfolioStore),
    );
  }

  private dispatchHydationStatusStore(
    hydrationStatusStore: IHydrationStatusStore,
  ) {
    console.log('dispatchHydationStatusStore');
    this.store.dispatch(HydrationStatusActions.AllAction(hydrationStatusStore));
  }

  private dispatchUserProfileStore(userProfileStore: IUserProfileStore) {
    console.log('dispatchUserProfileStore');
    this.store.dispatch(UserProfileActions.AllAction(userProfileStore));
  }
}
